import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { router } from './router'
import { i18n, loadMessages, getInitialLocale } from './i18n'
import './assets/css/main.css'

async function bootstrap() {
  const app = createApp(App)
  app.config.errorHandler = (err, _instance, info) => {
    // eslint-disable-next-line no-console
    console.error('[GlobalError]', info, err)
    import('./observability/faro').then(({ pushEvent }) => {
      pushEvent('vue_global_error', {
        message: err instanceof Error ? err.message : String(err),
        info,
        stack: err instanceof Error ? (err.stack ?? '') : '',
      })
    })
  }

  // 加载默认 locale 消息
  await loadMessages(getInitialLocale())
  document.documentElement.lang = getInitialLocale()

  app.use(createPinia())
  app.use(router)
  app.use(i18n)
  app.mount('#app')

  // 骨架屏移除策略：等待视频 canplaythrough 后再淡出骨架屏，
  // 避免「骨架屏已消失、视频未就绪、body 背景图仍在」导致的视觉闪烁。
  // 超时兜底：最长 10s，CDN 失败也能优雅降级。
  const timeout = window.setTimeout(applyAppReady, 10_000)

  function applyAppReady() {
    document.documentElement.classList.add('app-ready')
    const skeleton = document.querySelector<HTMLElement>('.skeleton')
    if (skeleton) {
      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName === 'opacity' && e.target === skeleton) {
          skeleton.remove()
          skeleton.removeEventListener('transitionend', onTransitionEnd)
        }
      }
      skeleton.addEventListener('transitionend', onTransitionEnd)
    }
  }

  function onVideoReady() {
    clearTimeout(timeout)
    applyAppReady()
  }

  // canplaythrough 触发时若视频数据已足够（缓存命中等）则直接应用
  function bindVideo(el: HTMLVideoElement) {
    el.addEventListener('canplaythrough', onVideoReady, { once: true })
    if (el.readyState >= 4 /* HAVE_ENOUGH_DATA */) {
      onVideoReady()
    }
  }

  // 等待初始路由解析，尽早拿到页面组件渲染出的 <video>
  await router.isReady()

  const existing = document.querySelector<HTMLVideoElement>('.video-background')
  if (existing) {
    bindVideo(existing)
  } else {
    // RouterView 异步挂载可能晚于 router.isReady()，用 MutationObserver 兜底
    const observer = new MutationObserver(() => {
      const el = document.querySelector<HTMLVideoElement>('.video-background')
      if (el) {
        observer.disconnect()
        bindVideo(el)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
  }

  // 初始化 Faro 监控
  import('./observability/faro').then(({ initFaro }) => {
    initFaro()
  })
}

bootstrap()
